import React, { useState, createContext, useContext } from 'react';
import { cn } from '@lqa-frontend/ui-system';
import type {
	ITableProps as TablePropsType,
	ITableHeaderProps,
	ITableBodyProps,
	ITableFooterProps,
	ITableRowProps,
	ITableHeadProps,
	ITableCellProps,
	ITableCaptionProps,
} from './types';
import { TableLoadingVariant } from './types';
import { TableEmpty, TableEmptyType } from '../table-empty';
import { TableLoading } from './TableLoading';
import { TableSkeletonRows } from './TableSkeletonRows';
import { TableScrollArea, useTableScroll } from './TableScrollArea';

const TableRowHoverContext = createContext(false);

const stickyRightShadowClass =
	"md:after:content-[''] after:absolute after:top-0 after:right-full after:w-12 after:h-full after:pointer-events-none after:z-[15] md:after:bg-[linear-gradient(90deg,transparent_0%,rgba(0,0,0,0.1)_100%)]";

const stickyLeftShadowClass =
	"md:after:content-[''] after:absolute after:top-0 after:left-full after:w-12 after:h-full after:pointer-events-none after:z-[15] md:after:bg-[linear-gradient(270deg,transparent_0%,rgba(0,0,0,0.1)_100%)]";

function getStickyClasses({
	sticky,
	stickyLeftOffset,
	isScrolled,
	isScrollEnd,
	isHeader,
}: {
	sticky?: 'left' | 'right';
	stickyLeftOffset?: number;
	isScrolled: boolean;
	isScrollEnd: boolean;
	isHeader: boolean;
}): (string | false | undefined)[] {
	const isStickyRight = sticky === 'right';
	const isStickyLeft = sticky === 'left';
	const left = isStickyLeft ? (stickyLeftOffset ?? 0) : undefined;
	const zIndex = isStickyLeft ? (left && left > 0 ? 'z-[11]' : 'z-[12]') : '';
	const bg = isHeader ? 'bg-brand-blue-100' : 'bg-base-1';
	return [
		isStickyRight && `sticky right-0 z-[11] ${bg}`,
		isStickyRight && !isScrollEnd && stickyRightShadowClass,
		isStickyLeft && `sticky ${bg}`,
		isStickyLeft && zIndex,
		isStickyLeft && left === 0 && 'left-0',
		isStickyLeft && (left ?? 0) > 0 && isScrolled && stickyLeftShadowClass,
	];
}

const alignClasses = {
	left: 'text-left',
	center: 'text-center',
	right: 'text-right',
} as const;

function Table({
	className,
	containerRef,
	children,
	...props
}: TablePropsType) {
	return (
		<TableScrollArea containerRef={containerRef}>
			<table
				className={cn('w-full min-w-max caption-bottom text-14', className)}
				data-slot="table"
				data-testid="table"
				{...props}
			>
				{children}
			</table>
		</TableScrollArea>
	);
}

function TableHeader({
	className,
	hasToolbar = false,
	...props
}: ITableHeaderProps) {
	return (
		<thead
			className={cn(
				'border-b border-brand-blue-300 bg-brand-blue-100 [&_tr]:border-b [&_tr]:border-brand-blue-300',
				hasToolbar && 'border-t border-brand-blue-300',
				className
			)}
			data-slot="table-header"
			data-testid="table-header"
			{...props}
		/>
	);
}

function hasGridClassName(className: string | undefined): boolean {
	if (className === undefined || className === '') {
		return false;
	}

	return className.split(/\s+/).includes('grid');
}

interface ITableBodyStatusRowProps {
	colSpan: number;
	isCssGridBody: boolean;
	children: React.ReactNode;
}

function TableBodyStatusRow({
	colSpan,
	isCssGridBody,
	children,
}: Readonly<ITableBodyStatusRowProps>) {
	return (
		<TableRow
			isHoverDisabled
			className={cn(
				'w-full border-b-0',
				isCssGridBody && 'col-span-full block'
			)}
		>
			<TableCell
				colSpan={colSpan}
				className={cn(
					'w-full whitespace-normal p-0 align-top',
					isCssGridBody && 'col-span-full block'
				)}
			>
				{children}
			</TableCell>
		</TableRow>
	);
}

function TableBody({
	className,
	isEmpty = false,
	emptyType = TableEmptyType.Default,
	emptyMessage,
	emptyColSpan = 100,
	emptyMinHeightPx,
	emptyTestId,
	isLoading = false,
	loadingVariant = TableLoadingVariant.Skeleton,
	skeletonRowCount,
	skeletonColumns,
	hasCheckboxColumn,
	loadingLabel = 'Loading table data',
	loadingMinHeightPx,
	loadingTestId,
	children,
	...props
}: ITableBodyProps) {
	const isCssGridBody = hasGridClassName(className);
	// A skeleton needs to know the table's shape; without columns this
	// low-level body cannot draw one, so it keeps the spinner.
	const canRenderSkeleton =
		loadingVariant === TableLoadingVariant.Skeleton &&
		skeletonColumns !== undefined &&
		skeletonColumns.length > 0 &&
		!isCssGridBody;

	const isStatusBody = (isLoading && !canRenderSkeleton) || isEmpty;

	let bodyContent: React.ReactNode = children;
	if (isLoading && canRenderSkeleton) {
		bodyContent = (
			<>
				{/* Announced once. `sr-only` on the row keeps it out of the
				    layout, so the body's height is exactly the height of the
				    skeleton rows and nothing shifts when data lands. */}
				<TableRow isHoverDisabled className="sr-only">
					<TableCell colSpan={emptyColSpan}>
						<span role="status">{loadingLabel}</span>
					</TableCell>
				</TableRow>
				<TableSkeletonRows
					columns={skeletonColumns}
					rowCount={skeletonRowCount}
					hasCheckboxColumn={hasCheckboxColumn}
				/>
			</>
		);
	} else if (isLoading) {
		bodyContent = (
			<TableBodyStatusRow colSpan={emptyColSpan} isCssGridBody={isCssGridBody}>
				<TableLoading
					minHeightPx={loadingMinHeightPx}
					data-testid={loadingTestId ?? 'table-loading'}
				/>
			</TableBodyStatusRow>
		);
	} else if (isEmpty) {
		bodyContent = (
			<TableBodyStatusRow colSpan={emptyColSpan} isCssGridBody={isCssGridBody}>
				<TableEmpty
					type={emptyType}
					message={emptyMessage}
					minHeightPx={emptyMinHeightPx}
					data-testid={emptyTestId ?? 'table-empty'}
				/>
			</TableBodyStatusRow>
		);
	}

	return (
		<tbody
			className={cn(
				'[&_tr:last-child]:border-b-0',
				className,
				isStatusBody && isCssGridBody && 'col-span-full block'
			)}
			aria-busy={isLoading || undefined}
			data-slot="table-body"
			data-testid="table-body"
			{...props}
		>
			{bodyContent}
		</tbody>
	);
}

function TableFooter({ className, ...props }: ITableFooterProps) {
	return (
		<tfoot
			className={cn(
				'border-t border-divider bg-base-2 font-medium [&>tr]:last:border-b-0',
				className
			)}
			data-slot="table-footer"
			data-testid="table-footer"
			{...props}
		/>
	);
}

function TableRow({
	className,
	isSelected,
	isRowHoverEnabled = false,
	isHoverDisabled = false,
	onMouseEnter,
	onMouseLeave,
	...props
}: ITableRowProps) {
	const [isHovered, setHovered] = useState(false);

	const handleMouseEnter = (e: React.MouseEvent<HTMLTableRowElement>) => {
		if (isRowHoverEnabled) setHovered(true);
		onMouseEnter?.(e);
	};
	const handleMouseLeave = (e: React.MouseEvent<HTMLTableRowElement>) => {
		if (isRowHoverEnabled) setHovered(false);
		onMouseLeave?.(e);
	};

	const rowBg = isHoverDisabled
		? undefined
		: isRowHoverEnabled
			? isHovered && 'bg-brand-blue-50'
			: 'hover:bg-brand-blue-50';

	return (
		<TableRowHoverContext.Provider
			value={isRowHoverEnabled && !isHoverDisabled ? isHovered : false}
		>
			<tr
				className={cn(
					'border-b border-divider transition-colors',
					rowBg,
					className
				)}
				data-slot="table-row"
				data-testid="table-row"
				data-selected={!!isSelected || undefined}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				{...props}
			/>
		</TableRowHoverContext.Provider>
	);
}

function TableHead({
	className,
	align = 'left',
	sticky,
	stickyLeftOffset,
	...props
}: ITableHeadProps) {
	const { isScrollEnd, isScrolled } = useTableScroll();
	const leftOffset = sticky === 'left' ? (stickyLeftOffset ?? 0) : undefined;
	return (
		<th
			className={cn(
				'h-40 px-12 align-middle font-medium whitespace-nowrap text-text-secondary',
				'[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-2',
				alignClasses[align],
				getStickyClasses({
					sticky,
					stickyLeftOffset,
					isScrolled,
					isScrollEnd,
					isHeader: true,
				}),
				className
			)}
			style={
				sticky === 'left' && (leftOffset ?? 0) > 0
					? { left: leftOffset }
					: undefined
			}
			data-slot="table-head"
			data-testid="table-head"
			data-sticky={sticky}
			{...props}
		/>
	);
}

function TableCell({
	className,
	align = 'left',
	sticky,
	stickyLeftOffset,
	...props
}: ITableCellProps) {
	const { isScrollEnd, isScrolled } = useTableScroll();
	const isRowHovered = useContext(TableRowHoverContext);
	const leftOffset = sticky === 'left' ? (stickyLeftOffset ?? 0) : undefined;
	const hoverBg = isRowHovered && 'bg-brand-blue-50';
	return (
		<td
			className={cn(
				'px-12 py-10 align-middle whitespace-nowrap text-text-primary',
				'[&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-2',
				alignClasses[align],
				getStickyClasses({
					sticky,
					stickyLeftOffset,
					isScrolled,
					isScrollEnd,
					isHeader: false,
				}),
				hoverBg,
				className
			)}
			style={
				sticky === 'left' && (leftOffset ?? 0) > 0
					? { left: leftOffset }
					: undefined
			}
			data-slot="table-cell"
			data-testid="table-cell"
			data-sticky={sticky}
			{...props}
		/>
	);
}

function TableCaption({ className, ...props }: ITableCaptionProps) {
	return (
		<caption
			className={cn('mt-16 text-14 text-text-secondary', className)}
			data-slot="table-caption"
			data-testid="table-caption"
			{...props}
		/>
	);
}

export {
	Table,
	TableHeader,
	TableBody,
	TableFooter,
	TableRow,
	TableHead,
	TableCell,
	TableCaption,
};
